import { isHeartbeatIdleChallenge, isHeartbeatPromptText } from "../auto-reply/heartbeat.js";
import { stripQuantumPalaceContext } from "../gateway/chat-sanitize.js";
import {
  hasOrchestrationInfrastructureMarkers,
  stripOrchestrationFromHistoryMessages,
  stripOrchestrationInfrastructure,
} from "../orchestration/orchestration-transcript.js";
import { isControlPlaneMessage, isFleetAnnounceText } from "../sessions/message-origin.js";

/** Marker prepended by prompt-budget when older turns are dropped. */
export const CONVERSATION_SUMMARY_HEADER = "[Conversation summary from older dropped turns]";
export const CONVERSATION_SUMMARY_FOOTER = "[End summary]";

const CONVERSATION_SUMMARY_BLOCK =
  /^\[Conversation summary from older dropped turns\]\r?\n[\s\S]*?\[End summary\](?:\r?\n)*/;

const EARLIER_SUMMARY_HEADER = /^\[Earlier conversation summary[^\]]*\]/;

export interface ConversationSummaryParts {
  summary: string | null;
  body: string;
}

/**
 * Split prompt-budget / offloader summary blocks from the visible user body.
 * Summary-only messages yield an empty body so the UI can show a muted chip.
 */
export function extractConversationSummary(text: string): ConversationSummaryParts {
  const raw = text ?? "";
  const trimmedStart = raw.trimStart();
  if (trimmedStart.startsWith(CONVERSATION_SUMMARY_HEADER)) {
    const match = trimmedStart.match(CONVERSATION_SUMMARY_BLOCK);
    if (!match) {
      return { summary: trimmedStart.trim(), body: "" };
    }
    return {
      summary: match[0].trim(),
      body: trimmedStart.slice(match[0].length).trim(),
    };
  }
  if (EARLIER_SUMMARY_HEADER.test(trimmedStart)) {
    // Context-offloader system marker: treat the whole message as summary.
    return { summary: raw.trim(), body: "" };
  }
  return { summary: null, body: raw };
}

export function hasConversationSummary(text: string): boolean {
  return extractConversationSummary(text).summary != null;
}

export function stripConversationSummary(text: string): string {
  return extractConversationSummary(text).body;
}

export function messageTextContent(message: unknown): string {
  const m = message as Record<string, unknown>;
  if (typeof m.content === "string") {
    return m.content;
  }
  if (Array.isArray(m.content)) {
    return m.content
      .map((item) => {
        const x = item as Record<string, unknown>;
        return typeof x.text === "string" ? x.text : "";
      })
      .join("\n");
  }
  if (typeof m.text === "string") {
    return m.text;
  }
  return "";
}

export function isHeartbeatAckOnly(text: string): boolean {
  const t = text.trim();
  // Idle work-challenge must remain visible in transcripts (not an ack).
  if (/^Are you sure no work needs to be done\?$/i.test(t)) {
    return false;
  }
  return /^HEARTBEAT_OK\.?$/i.test(t) || /^HEARTBEAT_OK\s*$/i.test(t);
}

export function isHeartbeatAckText(text: string | undefined | null): boolean {
  if (!text) {
    return false;
  }
  return isHeartbeatAckOnly(text);
}

export { isHeartbeatPromptText };

export function stripContinuityFromUserText(text: string): string {
  return stripQuantumPalaceContext(text).trim();
}

export function stripUserFacingChatText(raw: string): string {
  return stripConversationSummary(
    stripContinuityFromUserText(stripOrchestrationInfrastructure(raw)),
  ).trim();
}

export function shouldOmitOrchestrationOnlyUserText(text: string): boolean {
  const stripped = stripUserFacingChatText(text);
  if (!stripped) {
    return true;
  }
  return hasOrchestrationInfrastructureMarkers(text.trim()) && !stripped;
}

export { hasOrchestrationInfrastructureMarkers, stripOrchestrationInfrastructure };

/** Work-product inject labels surfaced in the operator chat column. */
const OPERATOR_INJECT_LABEL_RE = /^\[(fleet-complete|synthesis|subagent-result|file-changes(?:-fleet)?)\]/im;

const OPERATOR_INFRA_PREFIX_RE =
  /^\[(?:operator-milestone|fleet-plan|run|fleet-complete|synthesis|subagent-result|file-changes(?:-fleet)?)\]\s*/i;

/** Assistant injects written for the operator (status + work product), not model history. */
export function isOperatorFacingInjectText(text: string): boolean {
  return OPERATOR_INJECT_LABEL_RE.test((text ?? "").trim());
}

/** Milestone injects from operator-trust-loop (progress pings, not work product). */
export function isOperatorMilestoneInjectText(text: string): boolean {
  return /^\[operator-milestone\]/im.test((text ?? "").trim());
}

/** Strip control-plane label prefixes for operator-facing display. */
export function stripOperatorInjectDisplayText(text: string): string {
  let t = (text ?? "").trim();
  while (OPERATOR_INFRA_PREFIX_RE.test(t)) {
    t = t.replace(OPERATOR_INFRA_PREFIX_RE, "").trim();
  }
  return t;
}

/**
 * The task name an announce refers to, e.g.
 * `879a7d90:Investigate magic-link login flow and lo`.
 *
 * `extractBackgroundTaskResultBody` discards the announce header, which
 * throws away the one line that identifies *which* unit produced a report.
 * Once reports land collapsed the operator has nothing else to tell two
 * reports apart, so the label is extracted before the header is stripped.
 * Returns `null` when the text carries no announce header.
 */
export function extractBackgroundTaskResultLabel(text: string): string | null {
  const raw = (text ?? "").trim();
  const match =
    /A background task\s+"([^"]+)"\s+just completed/i.exec(raw) ??
    /background task\s+"([^"]+)"/i.exec(raw);
  const label = match?.[1]?.trim() ?? "";
  return label || null;
}

/**
 * Body of a "A background task … just completed" announce after the header.
 * Empty / "(no output)" announces stay hidden; evidence-bearing ones surface.
 */
export function extractBackgroundTaskResultBody(text: string): string | null {
  const raw = (text ?? "").trim();
  if (!raw) {
    return null;
  }
  if (!isFleetAnnounceText(raw) && !/^A background task\b/i.test(raw)) {
    return null;
  }
  if (/^\[subagent-result\]/i.test(raw)) {
    const body = raw.replace(/^\[subagent-result\]\s*/i, "").trim();
    return body || null;
  }
  const lines = raw.split(/\r?\n/);
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const isHeader = /background task/i.test(line) || /just completed successfully/i.test(line);
    const isResultLead = /^\s*(Status|Evidence)\b/i.test(line);
    if (!isHeader || isResultLead) {
      break;
    }
    i += 1;
  }
  if (i < lines.length && (lines[i] ?? "").trim() === "") {
    i += 1;
  }
  const body = lines.slice(i).join("\n").trim();
  if (!body || /^\(no output\)\s*$/i.test(body)) {
    return null;
  }
  return body;
}

export function isOperatorFacingTranscriptMessage(message: unknown): boolean {
  const text = messageTextContent(message);
  if (isOperatorMilestoneInjectText(text)) {
    return false;
  }
  if (isOperatorFacingInjectText(text)) {
    return true;
  }
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  if (role === "user" && extractBackgroundTaskResultBody(text)) {
    return true;
  }
  return false;
}

/** True when an operator inject carries no work product (milestones, empty bodies). */
export function shouldOmitOperatorInjectText(text: string): boolean {
  const raw = (text ?? "").trim();
  if (!raw) {
    return true;
  }
  if (isOperatorMilestoneInjectText(raw)) {
    return true;
  }
  if (/^\[orchestration\]/i.test(raw)) {
    return true;
  }
  if (!isOperatorFacingInjectText(raw)) {
    return false;
  }
  const display = stripOperatorInjectDisplayText(raw).trim();
  return !display || /^\(no output\)$/i.test(display);
}

export function shouldOmitChatToolTranscriptMessage(message: unknown): boolean {
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  if (role !== "toolresult" && role !== "tool") {
    return false;
  }
  const toolName = (
    typeof m.toolName === "string" ? m.toolName : typeof m.tool_name === "string" ? m.tool_name : ""
  )
    .trim()
    .toLowerCase();
  if (toolName !== "read") {
    return false;
  }
  const haystack = messageTextContent(message).toLowerCase();
  return haystack.includes("heartbeat.md");
}

export function shouldOmitChatTranscriptMessage(message: unknown): boolean {
  if (!message) {
    return false;
  }
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  const text = messageTextContent(message);
  if (shouldOmitOperatorInjectText(text)) {
    return true;
  }
  // Heartbeat / empty announces stay out of the chat column. Operator-facing
  // injects (fleet-complete, subagent-result, file-changes) and evidence-bearing
  // background-task announces must remain visible — they are the work product.
  if (
    isControlPlaneMessage(message) &&
    !hasConversationSummary(text) &&
    !isOperatorFacingTranscriptMessage(message)
  ) {
    return true;
  }
  if (role === "assistant") {
    if (isHeartbeatAckOnly(text) || isHeartbeatIdleChallenge(text)) {
      return true;
    }
  }
  if (role === "user") {
    const raw = messageTextContent(message);
    // Keep prompt-budget summaries so the UI can render a muted chip.
    if (hasConversationSummary(raw)) {
      return false;
    }
    if (shouldOmitOrchestrationOnlyUserText(raw)) {
      return true;
    }
    const stripped = stripContinuityFromUserText(stripOrchestrationInfrastructure(raw));
    if (!stripped) {
      return true;
    }
    if (isHeartbeatPromptText(stripped)) {
      return true;
    }
    if (stripped.startsWith("A new session was started via /new or /reset")) {
      return true;
    }
  }
  return false;
}

/**
 * Single operator-chat transcript projection (I3).
 * Used by `chat.history` and mirrored by the Control UI on live rows.
 */
export function projectOperatorChatTranscript<
  T extends { role?: string; content?: unknown; text?: unknown },
>(messages: T[]): T[] {
  const orchestrationStripped = stripOrchestrationBlocksFromAssistantReplies(messages);
  const orchestrationSanitized = stripOrchestrationFromHistoryMessages(orchestrationStripped);
  const visible = orchestrationSanitized.filter(
    (message) => !shouldOmitChatTranscriptMessage(message),
  );
  return dedupeOperatorInjectMessages(visible);
}

/**
 * Collapse runs of consecutive operator-injected assistant messages
 * (e.g. repeated `[fleet-complete]` notices from successive heartbeats)
 * down to the most recent one. The on-disk transcript keeps every entry
 * for forensic purposes — but the chat column should only surface the
 * latest status, otherwise the user sees a wall of `[fleet-complete] All N
 * tasks complete` rows that all reference state older than the next user
 * prompt.
 *
 * Operates on a best-effort key: matching role (assistant), matching
 * leading `OPERATOR_INJECT_LABEL_RE` label, and matching first line (the
 * task title). When the body's first line differs we treat the rows as
 * distinct announcements and keep both.
 */
export function dedupeConsecutiveOperatorInjectMessages<T>(messages: T[]): T[] {
  if (!Array.isArray(messages) || messages.length <= 1) {
    return messages;
  }
  const out: T[] = [];
  for (const message of messages) {
    const prev = out[out.length - 1] as Record<string, unknown> | undefined;
    const curr = message as Record<string, unknown> | undefined;
    if (
      prev &&
      curr &&
      typeof prev === "object" &&
      typeof curr === "object" &&
      (prev as Record<string, unknown>).role === "assistant" &&
      (curr as Record<string, unknown>).role === "assistant" &&
      messageKey(prev) === messageKey(curr)
    ) {
      // Replace the previous occurrence with the current (most recent) one.
      out[out.length - 1] = message;
      continue;
    }
    out.push(message);
  }
  return out;
}

const BACKGROUND_TASK_LABEL_RE = /A background task "([^"]+)"/i;
const TASK_HEX_PREFIX_RE = /^([a-f0-9]{6,16}):/i;

/** Stable task id from subagent-result inject bodies (hex prefix in task label). */
export function extractInjectTaskId(text: string): string | null {
  const raw = text ?? "";
  const taskMatch = BACKGROUND_TASK_LABEL_RE.exec(raw);
  const quotedLabel = taskMatch?.[1]?.trim() ?? "";
  const fromQuoted = TASK_HEX_PREFIX_RE.exec(quotedLabel)?.[1]?.toLowerCase();
  if (fromQuoted) {
    return fromQuoted;
  }
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const firstLine = lines[0] ?? "";
  const labelMatch = firstLine.match(OPERATOR_INJECT_LABEL_RE);
  if (labelMatch) {
    const afterLabel = firstLine.slice(labelMatch[0].length).trim();
    const fromSubject = TASK_HEX_PREFIX_RE.exec(afterLabel)?.[1]?.toLowerCase();
    if (fromSubject) {
      return fromSubject;
    }
  }
  return null;
}

function messageKey(message: Record<string, unknown>): string {
  const text = messageTextContent(message);
  if (!text.trim()) {
    return "";
  }
  const labelMatch = text.match(OPERATOR_INJECT_LABEL_RE);
  if (!labelMatch || !labelMatch[0]) {
    return "";
  }
  // Per-task dedupe: distinct workers sharing a generic subject line stay visible.
  if (/^\[subagent-result\]/i.test(labelMatch[0])) {
    const taskId = extractInjectTaskId(text);
    if (taskId) {
      return `[subagent-result]|task:${taskId}`;
    }
  }
  // The label is the first `[…]` token on the first non-blank line; the
  // rest of that line (after the closing `]`) is the announcement's
  // subject ("All 4 tasks complete", "worker-3 finished the PR"). We
  // compare subjects so two [fleet-complete] notes about different tasks
  // stay distinct instead of collapsing into a single stack entry.
  //
  // Production injects commonly put the subject on a new line after the
  // label (e.g. "[subagent-result]\n\n<actual body>"); for those we fall
  // through to the next non-blank line so the key carries a usable subject
  // instead of collapsing to an empty string that would falsely match
  // every other empty-subject inject.
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const firstLine = lines[0] ?? "";
  let subject = firstLine.slice(labelMatch[0].length).trim();
  if (!subject && lines.length > 1) {
    // First line was just the label; take the next non-blank line.
    subject = (lines[1] ?? "").slice(0, 80).trim();
  }
  return `${labelMatch[0]}|${subject.slice(0, 80)}`;
}

/**
 * Collapse runs of consecutive `[operator-milestone]` rows to the most
 * recent one — multiple progress pings for the same plan often arrive
 * back-to-back and would otherwise stack as a wall of duplicates in the
 * chat column. Distinct milestones (different first-line subjects)
 * stay distinct.
 *
 * Mirrors `dedupeConsecutiveOperatorInjectMessages` but restricted to
 * the milestone label so progress pings cannot accidentally collapse
 * work-product rows (`[subagent-result]`, `[fleet-complete]`,
 * `[file-changes]`).
 */
export function dedupeConsecutiveOperatorMilestones<T>(messages: T[]): T[] {
  if (!Array.isArray(messages) || messages.length <= 1) {
    return messages;
  }
  const out: T[] = [];
  for (const message of messages) {
    const prev = out[out.length - 1] as Record<string, unknown> | undefined;
    const curr = message as Record<string, unknown> | undefined;
    if (
      prev &&
      curr &&
      typeof prev === "object" &&
      typeof curr === "object" &&
      (prev as Record<string, unknown>).role === "assistant" &&
      (curr as Record<string, unknown>).role === "assistant" &&
      isOperatorMilestoneInjectText(messageTextContent(prev)) &&
      isOperatorMilestoneInjectText(messageTextContent(curr)) &&
      milestoneSubject(prev) === milestoneSubject(curr)
    ) {
      out[out.length - 1] = message;
      continue;
    }
    out.push(message);
  }
  return out;
}

function milestoneSubject(message: Record<string, unknown>): string {
  const text = messageTextContent(message);
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  // Production injects put the label on its own line and the subject
  // on the next non-blank line (mirrors `stripOperatorInjectDisplayText`).
  // We use the first non-label line as the subject so two milestones with
  // different bodies do NOT collapse.
  const MILESTONE_LABEL_RE = /^\[operator-milestone\]\s*/i;
  let subject = "";
  for (const line of lines) {
    const cleaned = line.replace(MILESTONE_LABEL_RE, "").trim();
    if (cleaned) {
      subject = cleaned;
      break;
    }
  }
  return subject.slice(0, 96);
}

function patchMessageText(
  message: Record<string, unknown>,
  stripped: string,
): Record<string, unknown> {
  if (typeof message.content === "string") {
    return { ...message, content: stripped };
  }
  if (Array.isArray(message.content)) {
    const next = message.content.map((item, index) => {
      if (index !== 0) {
        return item;
      }
      const entry = item as Record<string, unknown>;
      if (entry.type === "text" && typeof entry.text === "string") {
        return { ...entry, text: stripped };
      }
      return item;
    });
    return { ...message, content: next };
  }
  if (typeof message.text === "string") {
    return { ...message, text: stripped };
  }
  return message;
}

/**
 * Strip `[orchestration] ## …` blocks that the parent assistant model
 * sometimes echoes verbatim in its own conversational text (not via
 * `chat.inject`). These blocks are infrastructure that should never
 * surface to the operator. Acts on assistant-role messages only;
 * leaves injects and user text untouched. Returns the original array
 * when nothing changes.
 */
export function stripOrchestrationBlocksFromAssistantReplies<
  T extends { role?: string; content?: unknown; text?: unknown },
>(messages: T[]): T[] {
  let changed = false;
  const next = messages.map((message) => {
    const role = typeof message.role === "string" ? message.role.toLowerCase() : "";
    if (role !== "assistant") {
      return message;
    }
    const raw = messageTextContent(message);
    if (!raw.includes("[orchestration]")) {
      return message;
    }
    const stripped = raw
      .replace(/\[orchestration\][\s\S]*?(?=\n{2,}|$)/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    if (!stripped || stripped === raw.trim()) {
      return message;
    }
    changed = true;
    return patchMessageText(message as Record<string, unknown>, stripped) as T;
  });
  return changed ? next : messages;
}

/**
 * Evidence-bearing section headings inside an operator-inject body. When any
 * of these headings appear in the message body, the row is treated as the
 * actual work product and is never collapsed away by `dedupeOperatorInjectMessages`
 * — even if another row with the same subject survives later in the array.
 */
const EVIDENCE_BEARING_HEADING_RE =
  /(^|\n)#{1,6}\s+(Investigation|Findings?|Root Cause|Summary|Results?|Diff|Patch|Plan|Recommendation)\b/i;

/** True if the body of an operator-inject message carries evidence we must surface. */
function messageHasEvidence(message: Record<string, unknown>): boolean {
  const text = messageTextContent(message);
  if (!text) return false;
  // Strip the leading `[label]` prefix so the heading regex matches the body.
  const body = text.replace(/^\[[^\]]+\]\s*/, "");
  return EVIDENCE_BEARING_HEADING_RE.test(body);
}

/**
 * Like {@link dedupeConsecutiveOperatorInjectMessages}, but folds rows globally
 * (not just when adjacent): when two assistant injects share the same label +
 * subject, only the most recent survives. Evidence-bearing rows (those
 * containing section headings like `## Investigation`, `## Findings`, etc.)
 * are pinned — once seen, they are never replaced by a later same-subject row.
 *
 * Why global instead of adjacent: the chat history window otherwise stacks
 * `[subagent-result]` notices from successive sub-tasks one-per-row. The
 * operator asked to see the most recent status per subject, not every
 * intermediate result.
 *
 * Non-inject assistant rows still act as separators (they reset the "last
 * key" pointer) so a real assistant reply between two injects does not get
 * silently absorbed into the dedupe.
 */
export function dedupeOperatorInjectMessages<T>(messages: T[]): T[] {
  if (!Array.isArray(messages) || messages.length <= 1) {
    return messages;
  }
  const out: T[] = [];
  // Map of key -> last index in `out` for that key, so we can rewrite the
  // pinned slot when a later non-evidence same-subject row arrives.
  const lastIndexByKey = new Map<string, number>();
  for (const message of messages) {
    const curr = message as Record<string, unknown> | undefined;
    const role = typeof curr?.role === "string" ? curr.role.toLowerCase() : "";
    if (role === "assistant" && curr) {
      const key = messageKey(curr);
      if (key) {
        const prevIdx = lastIndexByKey.get(key);
        if (prevIdx !== undefined) {
          const prev = out[prevIdx] as Record<string, unknown>;
          // If the previously kept row is evidence-bearing, never replace it.
          if (messageHasEvidence(prev)) {
            continue;
          }
          // If the new row is evidence-bearing, replace unconditionally.
          if (messageHasEvidence(curr)) {
            out[prevIdx] = message;
            continue;
          }
          // Neither is evidence-bearing: replace the older with the newer.
          out[prevIdx] = message;
          continue;
        }
        lastIndexByKey.set(key, out.length);
        out.push(message);
        continue;
      }
    }
    // Non-inject assistant, or non-assistant row: reset the last-key pointer
    // for any keys whose pinned index points at this row's neighbors would be
    // wrong. Conservative approach: clear the map for keys whose previous
    // occurrence is the same subject as the current row's predecessor;
    // simpler: clear all (the cost is just re-insertion of later matches).
    if (role !== "assistant") {
      lastIndexByKey.clear();
    }
    out.push(message);
  }
  return out;
}

export function resolveChatHeartbeatActive(params: {
  messages: unknown[];
  runActive: boolean;
}): boolean {
  if (params.runActive) {
    return false;
  }
  const history = Array.isArray(params.messages) ? params.messages : [];
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const msg = history[i];
    const m = msg as Record<string, unknown>;
    const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
    if (role === "assistant") {
      return isHeartbeatAckOnly(messageTextContent(msg));
    }
    if (role === "user" || role === "system") {
      continue;
    }
    if (role === "toolresult" || role === "tool") {
      continue;
    }
    break;
  }
  return false;
}
